import { useEffect, useState } from "react";
import { useAuth } from "@repo/ping-react-js";
import { fetchUsage, type UsageResponse } from "../api/usage";
import UsageChart from "../components/UsageChart";
import { PALETTE, SOURCES } from "../components/usageColors";
import "./UsagePage.scss";

const RANGE_OPTIONS = [
  { label: "5 minutes", value: "-5m" },
  { label: "1 hour", value: "-1h" },
  { label: "12 hours", value: "-12h" },
  { label: "24 hours", value: "-24h" },
  { label: "7 days", value: "-7d" },
  { label: "30 days", value: "-30d" },
  { label: "90 days", value: "-90d" },
];
const number = (value: number) => value.toLocaleString();

const UsagePage = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState<UsageResponse | null>(null);
  const [settledRequest, setSettledRequest] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState("-30d");
  const [selectedSource, setSelectedSource] = useState("__all__");
  const [detailsContainer, setDetailsContainer] = useState<HTMLDivElement | null>(null);

  const requestKey = `${accessToken}:${range}`;
  const loading = settledRequest !== requestKey;

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    fetchUsage(accessToken, range)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setSettledRequest(requestKey);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, range, requestKey]);

  const sourceKeys = data
    ? Object.keys(data.sources).sort((a, b) => data.sources[b].count - data.sources[a].count)
    : [];
  // Alphabetical order keeps the chart and source list in agreement when filtering.
  const colors = Object.fromEntries([...sourceKeys].sort().map((key, i) => [key, PALETTE[i % PALETTE.length]]));
  const source = sourceKeys.includes(selectedSource) ? selectedSource : "__all__";
  const total = data ? (source === "__all__" ? data.total.count : data.sources[source].count) : 0;
  const maxCount = Math.max(1, ...sourceKeys.map((key) => data!.sources[key].count));
  const hasNegative = sourceKeys.some((key) => data!.sources[key].count < 0);

  return (
    <>
      <header className="app-header">
        <h1>Usage</h1>
      </header>
      <div className="app-content">
        <div className="usage-page">
          <div className="usage-toolbar">
            <div>
              <h2>Usage overview</h2>
              <p>Explore your activity across data sources.</p>
            </div>
            <label className="usage-range">
              Time range
              <select
                value={range}
                onChange={(event) => {
                  setRange(event.target.value);
                  setSelectedSource("__all__");
                }}
              >
                {RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    Last {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {loading && (
            <div className="usage-message" role="status">
              Loading usage…
            </div>
          )}
          {error && !loading && (
            <div className="usage-message usage-error" role="alert">
              Unable to load usage. {error}
            </div>
          )}
          {data && !loading && !error && (
            <>
              <section className="usage-activity" aria-label="Usage activity">
                <div className="usage-summary">
                  <div>
                    <div className="usage-eyebrow">
                      {source === "__all__" ? "Total usage" : `${SOURCES[source] ?? source} usage`}
                    </div>
                    <div className="usage-big-number">{number(total)}</div>
                    <p>Location-source units · includes reserved usage</p>
                    <label className="usage-filter">
                      Data source
                      <select value={source} onChange={(event) => setSelectedSource(event.target.value)}>
                        <option value="__all__">All sources</option>
                        {sourceKeys.map((key) => (
                          <option key={key} value={key}>
                            {SOURCES[key] ?? key}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {sourceKeys.length > 0 && (
                    <div className="usage-details-panel" ref={setDetailsContainer} aria-label="Activity details" />
                  )}
                </div>
                {sourceKeys.length ? (
                  <>
                    <div className="usage-chart">
                      <UsageChart
                        detailsContainer={detailsContainer}
                        buckets={data.buckets}
                        sourceKeys={source === "__all__" ? sourceKeys : [source]}
                        colors={colors}
                        granularity={data.granularity}
                        start={data.start}
                        end={data.end}
                      />
                    </div>
                    <p className="usage-chart-note">
                      {range === "-7d" || range === "-30d" || range === "-90d"
                        ? "Daily totals · UTC"
                        : "Activity over time · UTC"}
                      . Each location counts once per requested source.
                    </p>
                  </>
                ) : (
                  <div className="usage-message">No usage in this period. Try a longer time range.</div>
                )}
              </section>
              {sourceKeys.length > 0 && (
                <section className="usage-sources" aria-labelledby="usage-sources-title">
                  <div className="usage-section-heading">
                    <h2 id="usage-sources-title">Usage by source</h2>
                    <span>{sourceKeys.length} sources</span>
                  </div>
                  <p className="usage-section-help">Select a source to view its activity above.</p>
                  <div className="usage-source-table">
                    <div className="usage-source-columns">
                      <span>Data source</span>
                      <span>Usage</span>
                    </div>
                    {sourceKeys.map((key) => {
                      const count = data.sources[key].count;
                      return (
                        <button
                          key={key}
                          className={`usage-source-row ${source === key ? "is-selected" : ""}`}
                          aria-pressed={source === key}
                          onClick={() => setSelectedSource(source === key ? "__all__" : key)}
                        >
                          <span className="usage-source-name" title={`${SOURCES[key] ?? key} (${key})`}>
                            <span className="usage-source-dot" style={{ background: colors[key] }} />
                            <span>{SOURCES[key] ?? key}</span>
                          </span>
                          <span className="usage-source-meter" aria-hidden="true">
                            <span
                              style={{ width: `${(Math.max(0, count) / maxCount) * 100}%`, background: colors[key] }}
                            />
                          </span>
                          <span className={`usage-source-count ${count < 0 ? "is-negative" : ""}`}>
                            {number(count)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {hasNegative && (
                    <p className="usage-data-note">
                      Some sources have negative usage balances in the recorded data. These values are shown as
                      reported.
                    </p>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
export default UsagePage;
